import { z } from 'zod';
import {
  ErrorCode,
  PARTNER_NEWS_MAX_IMAGE_BYTES,
  PartnerNewsLinkUrlSchema,
  partnerNewsImageFormatOf,
  type CreatePartnerNewsRequest,
  type PartnerMaterialLocale,
  type PartnerNewsAction,
  type PartnerNewsAdmin,
  type PartnerNewsImageFormat,
  type PartnerNewsImageSlot,
  type UpdatePartnerNewsRequest
} from '@bliks-io/contracts';
import type { PartnerNewsSlide } from '@/components/partner-content/PartnerNewsStory';
import {
  LocalizedFormTextSchema,
  OptionalLocalizedFormTextSchema,
  compactText,
  optionalText,
  requiredText,
  toLocalizedFormText,
  type LocalizedFormText
} from '@/pages/admin/partner-content/localizedText';

const ActionFormSchema = z.object({
  url: z.string(),
  label: OptionalLocalizedFormTextSchema
});

export const NewsFormSchema = z
  .object({
    pinned: z.boolean(),
    eyebrow: OptionalLocalizedFormTextSchema,
    title: LocalizedFormTextSchema,
    body: LocalizedFormTextSchema,
    primaryAction: ActionFormSchema,
    secondaryAction: ActionFormSchema,
    footnote: OptionalLocalizedFormTextSchema
  })
  .superRefine((values, ctx) => {
    for (const name of ['primaryAction', 'secondaryAction'] as const) {
      const { url, label } = values[name];
      const hasUrl = Boolean(url.trim());
      if (hasUrl && !PartnerNewsLinkUrlSchema.safeParse(url.trim()).success) {
        ctx.addIssue({ code: 'custom', path: [name, 'url'], message: ErrorCode.FIELD_INVALID_URL });
      }
      // A button needs both: where it goes and, in German at least, what it says.
      if (hasUrl && !label.de.trim()) {
        ctx.addIssue({
          code: 'custom',
          path: [name, 'label', 'de'],
          message: ErrorCode.FIELD_REQUIRED
        });
      }
      if (!hasUrl && label.de.trim()) {
        ctx.addIssue({ code: 'custom', path: [name, 'url'], message: ErrorCode.FIELD_REQUIRED });
      }
    }
  });

export type NewsForm = z.infer<typeof NewsFormSchema>;

function toActionForm(action: PartnerNewsAction | undefined) {
  return { url: action?.url ?? '', label: toLocalizedFormText(action?.label) };
}

export function toNewsForm(news?: PartnerNewsAdmin | null): NewsForm {
  return {
    pinned: news?.pinned ?? false,
    eyebrow: toLocalizedFormText(news?.eyebrow),
    title: toLocalizedFormText(news?.title),
    body: toLocalizedFormText(news?.body),
    primaryAction: toActionForm(news?.primaryAction),
    secondaryAction: toActionForm(news?.secondaryAction),
    footnote: toLocalizedFormText(news?.footnote)
  };
}

function toAction(action: { url: string; label: LocalizedFormText }): PartnerNewsAction | null {
  const url = action.url.trim();
  return url ? { url, label: requiredText(action.label) } : null;
}

export function toCreateNewsRequest(values: NewsForm): CreatePartnerNewsRequest {
  const eyebrow = optionalText(values.eyebrow);
  const footnote = optionalText(values.footnote);
  const primaryAction = toAction(values.primaryAction);
  const secondaryAction = toAction(values.secondaryAction);
  return {
    pinned: values.pinned,
    title: requiredText(values.title),
    body: requiredText(values.body),
    ...(eyebrow ? { eyebrow } : {}),
    ...(primaryAction ? { primaryAction } : {}),
    ...(secondaryAction ? { secondaryAction } : {}),
    ...(footnote ? { footnote } : {})
  };
}

/** Sends emptied optional parts as null so the server removes them. */
export function toUpdateNewsRequest(values: NewsForm): UpdatePartnerNewsRequest {
  return {
    pinned: values.pinned,
    title: requiredText(values.title),
    body: requiredText(values.body),
    eyebrow: optionalText(values.eyebrow),
    primaryAction: toAction(values.primaryAction),
    secondaryAction: toAction(values.secondaryAction),
    footnote: optionalText(values.footnote)
  };
}

/** Picture changes held in the dialog until the editor saves. */
export interface PendingImages {
  uploads: Partial<Record<PartnerNewsImageSlot, File>>;
  removals: PartnerNewsImageSlot[];
}

export const NO_PENDING_IMAGES: PendingImages = { uploads: {}, removals: [] };

export type ImageRejection = 'format' | 'size';

export const IMAGE_REJECTION_KEYS: Record<ImageRejection, string> = {
  format: 'partnerContent.news.images.rejected.format',
  size: 'partnerContent.news.images.rejected.size'
};

/** Why a dropped file cannot go on a news card, or its format when it can. */
export function checkNewsImage(
  file: File
): { format: PartnerNewsImageFormat } | { rejected: ImageRejection } {
  const format = partnerNewsImageFormatOf(file.type);
  if (!format) return { rejected: 'format' };
  if (file.size > PARTNER_NEWS_MAX_IMAGE_BYTES) return { rejected: 'size' };
  return { format };
}

/**
 * The picture a slot shows in the preview: a picked file, else the stored
 * one unless it is being removed.
 */
export function previewImageUrl(
  news: PartnerNewsAdmin | null,
  pending: PendingImages,
  previews: Partial<Record<PartnerNewsImageSlot, string>>,
  slot: PartnerNewsImageSlot
): string | undefined {
  if (pending.uploads[slot]) return previews[slot];
  if (pending.removals.includes(slot)) return undefined;
  return news?.[slot]?.url;
}

/** The story card as partners will see it, built from the unsaved form. */
export function toNewsPreview(
  values: NewsForm,
  images: Partial<Record<PartnerNewsImageSlot, string>>,
  publishedAt: string
): PartnerNewsSlide {
  const validAction = (action: NewsForm['primaryAction']) => {
    const built = toAction(action);
    return built && PartnerNewsLinkUrlSchema.safeParse(built.url).success ? built : undefined;
  };
  return {
    id: 'preview',
    eyebrow: compactText(values.eyebrow),
    title: compactText(values.title),
    body: compactText(values.body),
    primaryAction: validAction(values.primaryAction),
    secondaryAction: validAction(values.secondaryAction),
    footnote: compactText(values.footnote),
    logoUrl: images.logo,
    imageUrl: images.image,
    publishedAt
  };
}

/** A language is done once title and text are there, half done with one of them. */
export function newsCompleteness(
  values: Pick<NewsForm, 'title' | 'body'>,
  locale: PartnerMaterialLocale
): 'complete' | 'partial' | 'empty' {
  const parts = [values.title[locale]?.trim(), values.body[locale]?.trim()].map(Boolean);
  if (parts.every(Boolean)) return 'complete';
  return parts.some(Boolean) ? 'partial' : 'empty';
}
